import { useState } from 'react';
import { MeasurementPlan, Position } from '../../../shared/types/protocol.ts';
import { MANUAL_MEASUREMENT_VARIANTS, ManualMeasurementVariant } from '../../../shared/utils/getManualMeasurementVariants.ts';
import { getVariantPositions } from './getVariantPositions.ts';


export type PositionFilterChoice = 'all' | ManualMeasurementVariant;

/**
 * Enables filtering positions by measurement variant.
 * Owns position filter selection, provides list of filter options and the visible positions after filtering.
 */
export function usePositionFilter(
    positions: Position[],
    measurementPlan: MeasurementPlan | undefined,
) {
    const [selectedFilter, setPositionFilter] = useState<PositionFilterChoice>('all');

    // Create filter options based on defined measurement variants
    const variants = MANUAL_MEASUREMENT_VARIANTS.map(name => {
        const variant = measurementPlan?.variants.find(it => it.name === name);
        return {
            name,
            positionIds: getVariantPositions(positions, variant),
        };
    });
    const availableVariants = variants.filter(variant => variant.positionIds.size > 0);
    const filterOptions: PositionFilterChoice[] = [
        'all',
        ...availableVariants.map(variant => variant.name),
    ];

    // Discard an unavailable choice so it cannot silently reactivate after a plan edit
    const positionFilter = filterOptions.includes(selectedFilter) ? selectedFilter : 'all';
    if (positionFilter !== selectedFilter) setPositionFilter(positionFilter);

    // Get visible positions
    const selectedVariant = availableVariants.find(variant => variant.name === positionFilter);
    const allPositionIds = new Set(positions.map(position => position.id));
    const visiblePositionIds = selectedVariant?.positionIds ?? allPositionIds;

    return {
        positionFilter,
        filterOptions,
        visiblePositionIds,
        setPositionFilter,
    };
}
